import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NavigationError, Router } from '@angular/router';
import { FirebaseService } from './commons/firebase.service';
import { PagseguroService } from './commons/pagseguro.service';
import { ScriptLoaderService } from './core/script-loader.service';
import { doc, getFirestore, onSnapshot } from 'firebase/firestore';
import { DeviceService } from './commons/device.service';
import { TranslocoService } from '@ngneat/transloco';
import { FullscreenModalComponent } from './core/fullscreen-modal/fullscreen-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';

import { FullscreenModalCameraComponent } from './core/fullscreen-modal-camera/fullscreen-modal-camera.component';
import { FullscreenModalContextualSearchComponent } from './core/fullscreen-modal-contextual-search/fullscreen-modal-contextual-search.component';
import { UploadListaComprasComponent } from './core/upload-lista-compras/upload-lista-compras.component';
import { StringService } from './commons/string.service';
import { MenuItem } from 'primeng/api';
import { TieredMenu } from 'primeng/tieredmenu';
interface Language {
  code: string;   // sigla, ex: pt-br
  label: string;  // nome exibido
  flag: string;   // caminho do svg
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {

  constructor(
    private router: Router,
    private scriptLoader: ScriptLoaderService,
    private formBuilder: UntypedFormBuilder,
    public firebase: FirebaseService,
    private deviceService: DeviceService,
    private dialog: MatDialog,
    private transloco: TranslocoService,
  ) {
    this.buildMenu();
    this.transloco.langChanges$.subscribe(() => {
      this.buildMenu();
    });

    // Rede de segurança de diagnóstico: sem isto, uma navegação que falha no
    // parsing do Router (ex.: caracteres reservados num segmento de rota
    // montado à mão) acontecia de forma inteiramente silenciosa — o clique
    // simplesmente não fazia nada, sem nenhum rastro (ver issue #145 / T-286).
    this.router.events.subscribe(event => {
      if (event instanceof NavigationError) {
        console.error('Falha de navegação (NavigationError):', event.url, event.error);
      }
    });
  }

  @ViewChild('menu') menu!: TieredMenu;

  items: MenuItem[] = [];
  languages: Language[] = [
    { code: 'pt-br', label: 'Português', flag: 'assets/images/flags/pt-br.svg' },
    { code: 'en-us', label: 'English', flag: 'assets/images/flags/en-us.svg' },
    { code: 'de-de', label: 'Deutsch', flag: 'assets/images/flags/de-de.svg' },
    { code: 'fr-fr', label: 'Français', flag: 'assets/images/flags/fr-fr.svg' },
    { code: 'es-al', label: 'Español', flag: 'assets/images/flags/es-al.svg' },
    { code: 'zh-cn', label: '简体中文', flag: 'assets/images/flags/zh-cn.svg' }
  ];

  public searchFormGroup!: UntypedFormGroup;
  recentlyProducts: Array<any>;
  topCities: Array<any>;
  openModalMobile: boolean = false;
  topNavItems: MenuItem[] = [];
  config = {
    top_nav: [
      {
        label: 'menu.negociacoes', // ou a chave de tradução 'menu_automotivo'
        route: 'negociacoes',
        scroll: false,
        children: []
      },
      {
        // Ponto de entrada da "Lista de Compras Corporativa" (E-40) vive
        // dentro do submenu "Corporativo" — sem `route` (abre um modal, não
        // navega), por isso usa `action` em vez de `route` (ver
        // mapToPrimeMenu), igual "Automotivo" vive dentro de "Setores".
        label: 'menu.corporativo',
        route: null,
        scroll: false,
        children: [
          {
            label: 'lista_compras_enviar_nova_botao',
            route: null,
            scroll: false,
            children: [],
            action: 'listaCompras',
          },
        ],
      },
      {
        label: 'menu.setores',
        route: null,
        scroll: false,
        children: [
          {
            label: 'menu.automotivo',
            route: 'automotivo',
            scroll: false,
            children: [],
          }
        ],
      },
    ]
  }



  currentLang!: Language;

  ngOnInit(): void {
    this.firebase.initApp();
    this.getRecentlyProducts()
    this.getTopCities()
    this.searchFormGroup = this.formBuilder.group({
      query: ['', [Validators.required]],
      uf: ['', [Validators.required]]
    }, {
      validators: []
    });

    this.scriptLoader.add('../assets/js/template.js');

    const savedLang = localStorage.getItem('lang') || 'pt-br';
    this.currentLang = this.languages.find(l => l.code === savedLang)!;
    this.transloco.setActiveLang(savedLang);
    this.topNavItems = this.mapToPrimeMenu(this.config.top_nav);
  }

  menuOpenTimeout: any;
  menuCloseTimeout: any;

  openMenuWithDelay(trigger: MatMenuTrigger) {
    clearTimeout(this.menuCloseTimeout);
    this.menuOpenTimeout = setTimeout(() => trigger.openMenu(), 200); // abre depois de 200ms
  }

  closeMenuWithDelay(trigger: MatMenuTrigger) {
    clearTimeout(this.menuOpenTimeout);
    this.menuCloseTimeout = setTimeout(() => trigger.closeMenu(), 300); // fecha depois de 300ms
  }

  getRecentlyProducts() {
    const firestore = getFirestore(this.firebase.app);
    this.recentlyProducts = []
    onSnapshot(doc(firestore, 'recently_products', 'TPKfVqCoh5obQYXDxtvX'), (doc) => {
      const snapshot = doc.data()
      this.recentlyProducts[0] = { ...snapshot }
    });
    onSnapshot(doc(firestore, 'recently_products', 'xMmZqNAUbsCb3pszDR8W'), (doc) => {
      const snapshot = doc.data()
      this.recentlyProducts[1] = { ...snapshot }
    });
  }

  getTopCities() {
    const firestore = getFirestore(this.firebase.app);
    this.topCities = []
    onSnapshot(doc(firestore, 'TopCities', 'topCitiesData'), (doc) => {
      const cities = []
      const snapshot = doc.data()
      cities.push(...snapshot['cities'])
      const topCitiesNames = []

      cities.forEach(item => {
        topCitiesNames.push(item.city)
      })

      for (let i = 0; i < topCitiesNames.length; i += 3) {
        this.topCities.push(topCitiesNames.slice(i, i + 3));
      }
    });
  }

  pesquisar() {
    if (this.searchFormGroup.controls['query'].value.trim() !== '')
      this.router.navigateByUrl(`/produtos/${this.searchFormGroup.controls['query'].value}`);
  }

  trocarLingua(code: string) {
    localStorage.setItem('lang', code);
    this.transloco.setActiveLang(code);
    location.reload();
  }

  sair() {
    // Alinhado ao login: quem autentica agora é o FirebaseService (Auth modular,
    // emulator-aware); usar o AuthService (compat) aqui deixaria o logout sem
    // efeito sobre a sessão real (e sem limpar o __token__ do localStorage).
    this.firebase.sair().then(res => {
      this.router.navigateByUrl(`/welcome`);
    });
  }

  meusFavoritos() {
    this.router.navigateByUrl(`/minha-conta/favoritos`);
  }

  negociacoesAtivas() {
    this.router.navigateByUrl(`/minha-conta/negociacoes-ativas`);
  }

  minhasCompras() {
    this.router.navigateByUrl(`/minha-conta/compras`);
  }

  minhasVendas() {
    this.router.navigateByUrl(`/minha-conta/vendas`);
  }

  negociacoesExpiradas() {
    this.router.navigateByUrl(`/minha-conta/negociacoes-expiradas`);
  }

  meusProdutos() {
    this.router.navigateByUrl(`/minha-conta/meus-produtos`);
  }
  categoriasProdutos() {
    this.router.navigateByUrl(`/minha-conta/categorias-produtos`);
  }

  minhaContaConfig() {
    this.router.navigateByUrl(`/minha-conta`);
  }

  minhaGaragemConfig() {
    this.router.navigateByUrl(`/minha-conta/garagem`);
  }

  myNegotiationsBids() {
    this.router.navigateByUrl('/minha-conta/negociacoes-lances')
  }
  toAbbreviateName(name: string) {
    return (new StringService()).abbreviateName(name, { locale: 'pt-BR' })
  }
  acesso() {
    this.router.navigateByUrl(`/acesso`);
  }

  registro() {
    this.router.navigateByUrl(`/registro`);
  }

  navbarDropdownClick(e) {
    const probableElmToggleSubmenu = e.target?.nextElementSibling
    if (probableElmToggleSubmenu.classList.contains('rd-navbar-submenu-toggle')) {
      probableElmToggleSubmenu.click();
    }
  }

  title = 'Inversu';

  searchByBarcode() {
    this.openModal()
  }
  searchByPicture() {
    this.openModalCamera()
  }

  contextualSearch() {
    this.openModalContextualSearch()
  }

  listaCompras() {
    this.openModalUploadListaCompras()
  }

  openModal() {
    this.dialog.open(FullscreenModalComponent, {
      panelClass: 'fullscreen-dialog',
      backdropClass: 'fullscreen-backdrop',
      width: '100vw !important',
      height: '100vh !important',
    });
  }
  openModalContextualSearch() {
    this.dialog.open(FullscreenModalContextualSearchComponent, {
      panelClass: 'fullscreen-dialog',
      backdropClass: 'fullscreen-backdrop',
      width: '100vw !important',
      height: '100vh !important',
    });
  }

  openModalCamera() {
    this.dialog.open(FullscreenModalCameraComponent, {
      panelClass: 'fullscreen-dialog',
      backdropClass: 'fullscreen-backdrop',
    });
  }

  // Ponto de entrada da "Lista de Compras Corporativa" (upload de arquivo
  // Excel/CSV/PDF) — deliberadamente separado do chat de busca contextual
  // (`openModalContextualSearch`), não uma aba dentro dele (E-40).
  openModalUploadListaCompras() {
    this.dialog.open(UploadListaComprasComponent, {
      panelClass: 'fullscreen-dialog',
      backdropClass: 'fullscreen-backdrop',
    });
  }

  accessRecentlyProducts(link: string) {
    this.router.navigate([link], { skipLocationChange: false, replaceUrl: true }).then(() => {
      window.location.reload();
    }).catch(console.error)
  }

  navigate(item: { route: string }) {
    if (item.route) {
      this.router.navigate([item.route]);
    }
  }

  private mapToPrimeMenu(items: any[]): MenuItem[] {
    return items.map(item => ({
      label: this.transloco.translate(item.label),                    // ou this.translate.instant(item.label)
      labelKey: this.transloco.translate(item.label),                    // ou this.translate.instant(item.label)
      icon: item.icon || undefined,         // se quiser ícones depois
      routerLink: item.route || undefined,
      items: item.children?.length ? this.mapToPrimeMenu(item.children) : undefined,
      // `action` cobre itens que abrem um modal em vez de navegar por rota
      // simples (ex. "Lista de Compras", E-40) — `route` resolve os demais.
      command: item.action === 'listaCompras'
        ? () => this.listaCompras()
        : (item.route ? () => this.router.navigate([item.route]) : undefined),
      // se scroll: true → pode adicionar lógica aqui (ex: scroll para elemento)
    }));
  }
  private buildMenu() {
    console.log(this.transloco.translateObject('menu.negociacoes'));
    this.items = [
      {
        label: this.transloco.translate('menu.negociacoes'),
        routerLink: '/negociacoes'
      },
      {
        label: this.transloco.translate('menu.setores'),
        items: [
          {
            label: this.transloco.translate('menu.automotivo'),
            routerLink: '/automotivo'
          }
        ]
      }
      // ... outros itens do top_nav
    ];
  }
  toggle(event: Event) {
    this.menu.toggle(event);
  }
}

